import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Checkbox, Modal } from "@opendate-app/setlist";
import client from "../../../shared/api/client";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useStaffingScheduleContext } from "./StaffingScheduleContext";
import { addDays, weekLabel } from "./scheduleFormatters";

const countLabel = (count) => (count > 0 ? count : "—");
const changesLabel = (count) => `${count} ${count === 1 ? "change" : "changes"}`;

const Tile = ({ label, value, warning }) => (
  <div
    style={{
      padding: "10px 12px",
      borderRadius: "var(--sl-radius-md)",
      border: `1px solid ${warning ? "var(--sl-border-warning)" : "var(--sl-border-secondary)"}`,
      background: warning ? "var(--sl-bg-warning-primary)" : undefined,
    }}
  >
    <div
      style={{
        fontSize: 12,
        lineHeight: "18px",
        color: warning ? "var(--sl-text-warning)" : "var(--sl-text-quaternary)",
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontSize: 20,
        lineHeight: "30px",
        fontWeight: 600,
        color: warning ? "var(--sl-text-warning)" : undefined,
      }}
    >
      {value}
    </div>
  </div>
);

Tile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  warning: PropTypes.bool,
};

const PEOPLE_COLUMNS = "minmax(0, 1fr) 72px 72px 72px";

const PublishReviewModal = ({ preview, onClose, onPublished }) => {
  const { paths } = useStaffingScheduleContext();
  const [notify, setNotify] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    setPublishing(true);
    let response;
    try {
      response = await client.post(paths.publications, {
        ...preview.scope,
        notify,
      });
    } catch (error) {
      setPublishing(false);
      toastError(error?.response?.data?.errors?.[0] || "Could not publish this week.");
      return;
    }
    const { total, people } = response.data;
    toastSuccess(
      notify
        ? `Published ${changesLabel(total)}. ${people} staff will be notified.`
        : `Published ${changesLabel(total)} without notifications.`,
    );
    onPublished();
  };

  return (
    <Modal
      open
      width={560}
      title={`Publish ${weekLabel(preview.scope.week, addDays(preview.scope.week, 6))}`}
      description="Only staff with new, changed or removed shifts hear from you. One message each."
      onClose={onClose}
      footer={
        <div className="d-flex flex-wrap align-items-center w-100" style={{ gap: 12 }}>
          <Checkbox
            id="publish-notify"
            checked={notify}
            onChange={(event) => setNotify(event?.target ? event.target.checked : event)}
            label={
              <span className="d-flex flex-column">
                <span>Notify staff by SMS and email</span>
                <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
                  {notify
                    ? `${preview.people.length} staff get one message each.`
                    : "Shifts publish silently. Staff see them next time they check their schedule."}
                </span>
              </span>
            }
          />
          <div className="ml-auto d-flex" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={publishing} onClick={publish}>
              {`Publish ${changesLabel(preview.total)}`}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 16 }}>
        <Tile label="New" value={preview.new} />
        <Tile label="Changed" value={preview.changed} />
        <Tile label="Removed" value={preview.removed} />
        <Tile label="Open" value={preview.open} warning />
      </div>
      <div
        style={{
          border: "1px solid var(--sl-border-secondary)",
          borderRadius: "var(--sl-radius-md)",
          maxHeight: 280,
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: PEOPLE_COLUMNS,
            padding: "8px 12px",
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 500,
            color: "var(--sl-text-tertiary)",
            background: "var(--sl-bg-secondary)",
            borderBottom: "1px solid var(--sl-border-secondary)",
          }}
        >
          <span>{`Staff member (${preview.people.length})`}</span>
          <span>New</span>
          <span>Changed</span>
          <span>Removed</span>
        </div>
        {preview.people.map((person) => (
          <div
            key={person.id}
            style={{
              display: "grid",
              gridTemplateColumns: PEOPLE_COLUMNS,
              alignItems: "center",
              padding: "8px 12px",
              fontSize: 14,
              lineHeight: "20px",
              borderBottom: "1px solid var(--sl-border-secondary)",
            }}
          >
            <span className="d-flex flex-column" style={{ minWidth: 0 }}>
              <span style={{ fontWeight: 500 }}>{person.name}</span>
              <span style={{ fontSize: 12, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
                {person.channel}
              </span>
            </span>
            <span>{countLabel(person.added)}</span>
            <span>{countLabel(person.changed)}</span>
            <span>{countLabel(person.removed)}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
};

PublishReviewModal.propTypes = {
  preview: PropTypes.shape({
    new: PropTypes.number.isRequired,
    changed: PropTypes.number.isRequired,
    removed: PropTypes.number.isRequired,
    open: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    scope: PropTypes.shape({
      venue_ownership_id: PropTypes.number.isRequired,
      week: PropTypes.string.isRequired,
    }).isRequired,
    people: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        channel: PropTypes.string.isRequired,
        added: PropTypes.number.isRequired,
        changed: PropTypes.number.isRequired,
        removed: PropTypes.number.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onPublished: PropTypes.func.isRequired,
};

export default PublishReviewModal;
